import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useCallback } from 'react';
import projectService from '../services/projectService';

/**
 * Custom hook for Foundation Dashboard
 * Fetches all dashboard data in a single API call
 *
 * @param {string} projectId - Project ID
 * @returns {Object} Dashboard data and operations
 */
const useFoundationDashboard = (projectId) => {
  const queryClient = useQueryClient();

  // Fetch foundation dashboard data
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['foundationDashboard', projectId],
    () => projectService.getFoundationDashboard(projectId),
    {
      enabled: !!projectId,
      staleTime: 30000, // 30 seconds
    }
  );

  // Toggle task completion status
  const toggleTaskMutation = useMutation(
    (taskId) => projectService.toggleTaskStatus(projectId, taskId),
    {
      onSuccess: (result) => {
        // Update the dashboard cache with new completedTasks
        queryClient.setQueryData(['foundationDashboard', projectId], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            project: {
              ...oldData.project,
              completedTasks: result.project.completedTasks || result.project.completed_tasks
            }
          };
        });
      }
    }
  );

  // Check if a task has conversation
  const hasConversation = (taskId) => {
    return data?.tasksWithConversations?.includes(taskId) || false;
  };

  // Check if a task is completed
  const isTaskCompleted = (taskId) => {
    return data?.project?.completedTasks?.includes(taskId) || false;
  };

  // Update orchestrator data in cache (after save)
  const updateOrchestrator = useCallback((sectionId, orchestratorData) => {
    queryClient.setQueryData(['foundationDashboard', projectId], (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        sections: oldData.sections.map(section =>
          section._id === sectionId
            ? {
                ...section,
                orchestrator: {
                  ...section.orchestrator,
                  name: orchestratorData.name,
                  avatarUrl: orchestratorData.avatarUrl
                }
              }
            : section
        )
      };
    });
  }, [queryClient, projectId]);

  return {
    // Data
    project: data?.project || null,
    sections: data?.sections || [],
    tasksWithConversations: data?.tasksWithConversations || [],
    sectionAvatars: data?.sectionAvatars || {},

    // State
    isLoading,
    error,

    // Operations
    refetch,
    toggleTaskStatus: toggleTaskMutation.mutateAsync,
    isTogglingTask: toggleTaskMutation.isLoading,

    // Helpers
    hasConversation,
    isTaskCompleted,

    // Cache updates
    updateOrchestrator
  };
};

export default useFoundationDashboard;
